/** The Admin's Billing & usage read - `GET /api/v1/billing/usage`.
 *
 * **There is no billing in this product**, and this module is where that stays structural: it reads
 * what the workspace *has and holds* (the two entitlement facts an operator records, the data
 * region, how much evidence is stored, how many ledger entries exist) and nothing about money. The
 * server's response has no price, plan, invoice or payment field and a test pins its key set, so
 * there is nothing here to add one to.
 *
 * Admin-only on the server (`require_roles(Role.ADMIN)`), so the hook takes `enabled` and a
 * non-Admin's screen never asks: a request made only to be refused shows as a failed call in every
 * network log. A read that fails throws, never a default - an Admin shown "0 documents" because the
 * request dropped would be told something about the company that nobody measured.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type BillingUsage = components["schemas"]["BillingUsageOut"];

export const billingKeys = {
  usage: () => ["billing", "usage"] as const,
};

export function useBillingUsage(enabled: boolean) {
  return useQuery({
    queryKey: billingKeys.usage(),
    enabled,
    retry: false,
    queryFn: async (): Promise<BillingUsage> => {
      const { data, error, response } = await api.GET("/api/v1/billing/usage", {});
      if (!data) throw refusalFrom(response, error, "What this workspace has and uses could not be read.");
      return data;
    },
  });
}
